<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import DocumentSendDialog from './DocumentSendDialog.vue';
import DocumentAddDialog from './DocumentAddDialog.vue';
import DocumentPlaceSignaturesDialog from './DocumentPlaceSignaturesDialog.vue';

const props = defineProps({
  eventId: { type: [String, Number], default: null },
  documents: { type: Array, default: () => [] },
  libraryRecords: { type: Array, default: () => [] },
  libraries: { type: Array, default: () => [] },
  selectedLibraryId: { type: [String, Number], default: null },
  libraryLoading: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  showRemoveBlock: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggleCollapse',
  'removeBlock',
  'attach',
  'upload',
  'update',
  'remove',
  'send',
  'remind',
  'void',
  'refresh-library',
  'update:selectedLibraryId',
]);

const { t } = useI18n();
const showAddDialog = ref(false);
const sendDoc = ref(null);
const showSend = ref(false);
const placeDoc = ref(null);
const showPlace = ref(false);
const openMenuId = ref(null);

const addDocumentButtonClass =
  'w-full inline-flex items-center justify-center gap-2 h-11 px-4 text-sm font-medium rounded-lg bg-woot-50 text-woot-600 hover:bg-woot-75 dark:bg-woot-900/30 dark:text-woot-300 dark:hover:bg-woot-900/50 transition-colors disabled:opacity-50';

const menuActions = computed(() => {
  if (!props.showRemoveBlock) return [];
  return [
    {
      key: 'remove-block',
      label: t('PLANNING.SHOW.REMOVE_BLOCK'),
      icon: 'i-lucide-trash-2',
      danger: true,
    },
  ];
});

const attachedRecordIds = computed(() =>
  props.documents.map(d => d.record_id).filter(Boolean)
);

const statusLabel = status => {
  const key = `PLANNING.SHOW.DOCUMENT_STATUS_${String(status || '').toUpperCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const statusClass = status => {
  const map = {
    draft: 'bg-n-slate-3 text-n-slate-11',
    available: 'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-300',
    pending_signature:
      'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    viewed: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    signed:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    declined: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    expired: 'bg-n-slate-3 text-n-slate-11',
    voided: 'bg-n-slate-3 text-n-slate-11',
  };
  return map[status] || 'bg-n-slate-3 text-n-slate-11';
};

const canSend = doc =>
  doc.requires_signature &&
  ['draft', 'available', 'pending_signature', 'viewed'].includes(doc.status);

const canRemind = doc =>
  doc.requires_signature &&
  ['pending_signature', 'viewed'].includes(doc.status);

const canVoid = doc =>
  ['pending_signature', 'viewed'].includes(doc.status);

const canToggleSignature = doc =>
  !['signed', 'pending_signature', 'viewed'].includes(doc.status);

const isPdfDoc = doc => {
  if (!doc) return false;
  if (doc.is_pdf === true) return true;
  return /\.pdf$/i.test(doc.filename || doc.title || '');
};

const canPlaceSignatures = doc =>
  doc.requires_signature &&
  isPdfDoc(doc) &&
  ['draft', 'available', 'pending_signature', 'viewed'].includes(doc.status);

const needsSignatureFields = doc =>
  doc.requires_signature &&
  isPdfDoc(doc) &&
  !(Array.isArray(doc.signature_fields) && doc.signature_fields.length);

const primaryAction = doc => {
  if (props.readOnly || !doc || !canSend(doc)) return null;
  if (canRemind(doc)) {
    return {
      key: 'remind',
      label: t('PLANNING.SHOW.DOCUMENTS_REMIND'),
      icon: 'i-lucide-bell',
    };
  }
  return {
    key: 'send',
    label: t('PLANNING.SHOW.DOCUMENTS_SEND'),
    icon: 'i-lucide-send',
  };
};

const openAddDialog = () => {
  showAddDialog.value = true;
};

const openPlace = doc => {
  placeDoc.value = doc;
  showPlace.value = true;
};

const openSend = doc => {
  if (needsSignatureFields(doc)) {
    openPlace(doc);
    return;
  }
  sendDoc.value = doc;
  showSend.value = true;
};

const onPlaceSaved = updated => {
  // Dialog already persisted fields; sync parent list via a no-op-shaped update.
  emit('update', updated.id, {
    signature_fields: updated.signature_fields || [],
  });
};

const setMenuOpen = (docId, shown) => {
  openMenuId.value = shown ? docId : null;
};

const handlePrimaryAction = (doc, key) => {
  if (key === 'send' || key === 'remind') openSend(doc);
};

const handleRowMenu = (doc, key) => {
  openMenuId.value = null;
  if (key === 'download' && doc.source_url) {
    window.open(doc.source_url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'download_signed' && doc.signed_url) {
    window.open(doc.signed_url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (key === 'void') {
    emit('void', doc.id);
    return;
  }
  if (key === 'toggle_signature') {
    emit('update', doc.id, { requires_signature: !doc.requires_signature });
    return;
  }
  if (key === 'place_signatures') {
    openPlace(doc);
    return;
  }
  if (key === 'remove') emit('remove', doc.id);
};
</script>

<template>
  <SectionCard
    :title="t('PLANNING.SHOW.DOCUMENTS_TITLE')"
    icon="i-lucide-files"
    collapsible
    collapse-in-menu
    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
    :expand-label="t('PLANNING.SHOW.EXPAND')"
    :menu-actions="menuActions"
    flush
    :collapsed="collapsed"
    @toggle-collapse="emit('toggleCollapse')"
    @menu-action="action => action === 'remove-block' && emit('removeBlock')"
  >
    <div
      v-if="(loading || uploading) && !documents.length"
      class="flex flex-col items-center justify-center gap-3 py-8 pr-12"
    >
      <div
        class="size-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"
      />
      <p class="text-sm text-n-slate-11">
        {{
          uploading
            ? t('PLANNING.SHOW.DOCUMENTS_UPLOADING')
            : t('PLANNING.SHOW.DOCUMENTS_LOADING')
        }}
      </p>
    </div>

    <div v-else-if="!documents.length" class="py-8 text-center pr-12">
      <Icon icon="i-lucide-files" class="size-12 mx-auto text-woot-75 mb-2" />
      <p class="text-sm text-slate-500 font-semibold">
        {{ t('PLANNING.SHOW.DOCUMENTS_EMPTY') }}
      </p>
      <button
        v-if="!readOnly"
        type="button"
        class="mt-4 mx-auto max-w-xs print:hidden"
        :class="addDocumentButtonClass"
        :disabled="uploading"
        @click="openAddDialog"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
        {{ t('PLANNING.SHOW.DOCUMENTS_ADD') }}
      </button>
    </div>

    <div v-else class="min-w-0 pt-1">
      <div
        v-if="uploading"
        class="mb-3 flex items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2.5 text-sm text-n-slate-11"
      >
        <div
          class="size-4 shrink-0 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
        />
        {{ t('PLANNING.SHOW.DOCUMENTS_UPLOADING') }}
      </div>

      <div class="flex flex-col gap-2 p-1">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="rounded-lg border border-n-weak bg-white dark:bg-slate-950"
        >
          <div class="flex w-full min-w-0 items-center gap-3 px-3 py-3">
            <div class="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5">
              <div class="flex min-w-0 items-center gap-2">
                <span class="min-w-0 truncate text-sm font-semibold text-n-slate-12">
                  {{ doc.title }}
                </span>
                <span
                  class="inline-flex shrink-0 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold"
                  :class="statusClass(doc.status)"
                >
                  {{ statusLabel(doc.status) }}
                </span>
                <span
                  v-if="doc.requires_signature"
                  class="inline-flex shrink-0 rounded-full bg-n-slate-3 px-1.5 py-0.5 text-[0.65rem] font-semibold text-n-slate-11"
                >
                  {{ t('PLANNING.SHOW.DOCUMENTS_NEEDS_SIGNATURE') }}
                </span>
                <span
                  v-if="needsSignatureFields(doc)"
                  class="inline-flex shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[0.65rem] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                >
                  {{ t('PLANNING.SHOW.DOCUMENTS_PLACE_MISSING') }}
                </span>
              </div>
              <span class="truncate text-[11px] text-n-slate-11">
                {{ doc.filename }}
              </span>
            </div>

            <div
              v-if="!readOnly"
              class="flex shrink-0 items-center gap-1 print:hidden"
            >
              <Button
                v-if="primaryAction(doc)"
                size="sm"
                color="blue"
                type="button"
                class="justify-center px-3"
                :icon="primaryAction(doc).icon"
                :label="primaryAction(doc).label"
                @click="handlePrimaryAction(doc, primaryAction(doc).key)"
              />

              <VDropdown
                :shown="openMenuId === doc.id"
                :triggers="['click']"
                placement="bottom-end"
                :distance="8"
                :arrow="false"
                popper-class="event-menu-popper"
                @update:shown="shown => setMenuOpen(doc.id, shown)"
              >
                <button
                  type="button"
                  class="flex items-center justify-center size-8 rounded text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12"
                  :aria-label="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
                >
                  <Icon icon="i-lucide-ellipsis" class="size-5 shrink-0" />
                </button>
                <template #popper>
                  <div
                    class="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-75 dark:border-slate-800 p-1.5 flex flex-col min-w-44"
                    @click.stop
                    @mousedown.stop
                  >
                    <button
                      v-if="doc.source_url"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(doc, 'download')"
                    >
                      <Icon icon="i-lucide-download" class="size-5 text-n-slate-11" />
                      {{ t('PLANNING.SHOW.DOCUMENTS_DOWNLOAD') }}
                    </button>
                    <button
                      v-if="doc.signed_url"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(doc, 'download_signed')"
                    >
                      <Icon icon="i-lucide-file-check" class="size-5 text-n-slate-11" />
                      {{ t('PLANNING.SHOW.DOCUMENTS_DOWNLOAD_SIGNED') }}
                    </button>
                    <button
                      v-if="canToggleSignature(doc)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(doc, 'toggle_signature')"
                    >
                      <Icon icon="i-lucide-pen-line" class="size-5 text-n-slate-11" />
                      {{
                        doc.requires_signature
                          ? t('PLANNING.SHOW.DOCUMENTS_CLEAR_SIGNATURE')
                          : t('PLANNING.SHOW.DOCUMENTS_REQUIRE_SIGNATURE')
                      }}
                    </button>
                    <button
                      v-if="canPlaceSignatures(doc)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(doc, 'place_signatures')"
                    >
                      <Icon icon="i-lucide-signature" class="size-5 text-n-slate-11" />
                      {{ t('PLANNING.SHOW.DOCUMENTS_PLACE_SIGNATURES') }}
                    </button>
                    <button
                      v-if="canVoid(doc)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(doc, 'void')"
                    >
                      <Icon icon="i-lucide-ban" class="size-5 text-n-slate-11" />
                      {{ t('PLANNING.SHOW.DOCUMENTS_VOID') }}
                    </button>
                    <button
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2.5 text-red-600"
                      @click="handleRowMenu(doc, 'remove')"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-5" />
                      {{ t('PLANNING.SHOW.DOCUMENTS_REMOVE') }}
                    </button>
                  </div>
                </template>
              </VDropdown>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!readOnly" class="mt-3 pr-12 print:hidden">
        <button
          type="button"
          :class="addDocumentButtonClass"
          :disabled="uploading"
          @click="openAddDialog"
        >
          <Icon icon="i-lucide-plus" class="size-4" />
          {{ t('PLANNING.SHOW.DOCUMENTS_ADD') }}
        </button>
      </div>
    </div>

    <DocumentAddDialog
      v-model="showAddDialog"
      :libraries="libraries"
      :library-records="libraryRecords"
      :selected-library-id="selectedLibraryId"
      :library-loading="libraryLoading"
      :attached-record-ids="attachedRecordIds"
      :uploading="uploading"
      @update:selected-library-id="
        id => emit('update:selectedLibraryId', id)
      "
      @refresh-library="id => emit('refresh-library', id)"
      @attach="payload => emit('attach', payload)"
      @upload="payload => emit('upload', payload)"
    />

    <DocumentSendDialog
      v-model="showSend"
      :event-id="eventId"
      :document="sendDoc"
      @sent="doc => emit('send', doc)"
    />

    <DocumentPlaceSignaturesDialog
      v-model="showPlace"
      :event-id="eventId"
      :document="placeDoc"
      @saved="onPlaceSaved"
    />
  </SectionCard>
</template>
